// Lightweight Web Audio helper to control volume reliably on mobile (iOS)
// without relying on HTMLMediaElement.volume (which is restricted on iOS).

type GainEntry = {
  source: MediaElementAudioSourceNode;
  gain: GainNode;
};

let audioCtx: AudioContext | null = null;
const webkitAudioContext: any = (window as any).webkitAudioContext;
const getContext = (): AudioContext | null => {
  if (!audioCtx) {
    try {
      const Ctx = (window as any).AudioContext || webkitAudioContext;
      if (Ctx) audioCtx = new Ctx();
    } catch (e) {
      audioCtx = null;
    }
  }
  return audioCtx;
};

const gainMap = new WeakMap<HTMLMediaElement, GainEntry>();

export const resumeAudioContext = async () => {
  const ctx = getContext();
  if (ctx && ctx.state !== 'running') {
    try { await ctx.resume(); } catch {}
  }
};

export const attachGainToMediaElement = (el: HTMLMediaElement): GainNode | null => {
  const ctx = getContext();
  if (!ctx || !el) return null;
  const existing = gainMap.get(el);
  if (existing) return existing.gain;
  try {
    const source = ctx.createMediaElementSource(el);
    const gain = ctx.createGain();
    source.connect(gain);
    gain.connect(ctx.destination);
    gainMap.set(el, { source, gain });
    return gain;
  } catch (e) {
    // Element may already be connected in some browsers; ignore
    return null;
  }
};

export const setGainForElement = (el: HTMLMediaElement, value: number) => {
  const g = attachGainToMediaElement(el);
  if (g) {
    try {
      g.gain.value = Math.max(0, Math.min(1, value));
    } catch {}
  } else {
    // Fallback for environments without WebAudio
    try { (el as any).volume = Math.max(0, Math.min(1, value)); } catch {}
  }
};

export const getGainValueForElement = (el: HTMLMediaElement): number => {
  const entry = gainMap.get(el);
  if (entry) {
    try { return entry.gain.gain.value; } catch { return 1; }
  }
  // Fallback to element.volume when not attached
  try { return (el as any).volume ?? 1; } catch { return 1; }
};

